import { zodResolver } from '@hookform/resolvers/zod';
import { CreateWeightLogRequestSchema, type CreateWeightLogRequest } from '@onflow/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import {
  useCreateWeightLog,
  useDeleteWeightLog,
  useUpdateWeightLog,
  useWeightLogsQuery,
} from '../hooks/useWeightLogs.js';
import { todayISTKey } from '../lib/dateIST.js';
import { formatWeight, timeAgo } from '../lib/format.js';
import { toast } from '../lib/toast.js';

import { ArrowRight } from './v2/ArrowRight.js';

import '../styles/weight-log-widget.css';

export interface WeightLogWidgetProps {
  /**
   * When true, the widget renders nothing if the user has already logged
   * a weight on the current IST calendar day. Used on the dashboard so the
   * widget vanishes once today's weight is captured. Defaults to false on
   * Profile, where the widget is always visible.
   */
  hideWhenLoggedToday?: boolean;
  /**
   * When true, the card fills its container instead of the 320px cap used
   * for the dashboard header row. Used on Profile, where the widget stacks
   * above a full-width details table — the cap made the "Last logged" line
   * wrap to two rows and left the wide table butting flush against a narrow
   * box. Also adds a bottom margin so it clears the following section.
   */
  fullWidth?: boolean;
}

/**
 * Compact body-weight logging widget. On the dashboard it sits to the right
 * of the welcome header (>= 720px) or stacked below (< 720px) via
 * .dashboard-header-row, and disappears once today's weight is logged. On
 * the profile page it renders permanently as a quick-log surface.
 */
export function WeightLogWidget({
  hideWhenLoggedToday = false,
  fullWidth = false,
}: WeightLogWidgetProps = {}) {
  const lastQuery = useWeightLogsQuery({ page: 1, pageSize: 1 });
  const createMutation = useCreateWeightLog();
  const updateMutation = useUpdateWeightLog();
  const deleteMutation = useDeleteWeightLog();
  const [editingId, setEditingId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<CreateWeightLogRequest>({
    resolver: zodResolver(CreateWeightLogRequestSchema),
  });

  async function onSubmit(values: CreateWeightLogRequest) {
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, patch: { weightKg: values.weightKg } });
        setEditingId(null);
      } else {
        await createMutation.mutateAsync({ weightKg: values.weightKg });
      }
      reset({ weightKg: undefined as unknown as number });
      toast.success(editingId ? 'Corrected' : 'Logged');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to log weight');
    }
  }

  const last = lastQuery.data?.items[0] ?? null;
  const submitting = isSubmitting || createMutation.isPending || updateMutation.isPending;

  // Hide on dashboard once today's weight is logged. Computed from the
  // existing lastQuery (no extra fetch); uses IST calendar day so the
  // hide/show flips at midnight IST rather than 24 hours after the last log.
  const loggedToday = last !== null && todayISTKey(new Date(last.loggedAt)) === todayISTKey();
  if (hideWhenLoggedToday && loggedToday) return null;

  // Custom 0.1 kg stepper (native number-input spinners are hidden in CSS).
  // Empty field seeds from the last logged weight, then 70 as a neutral base.
  function step(dir: 1 | -1) {
    const cur = getValues('weightKg');
    const base = typeof cur === 'number' && !Number.isNaN(cur) ? cur : (last?.weightKg ?? 70);
    const next = Math.min(300, Math.max(20, Math.round((base + dir * 0.1) * 10) / 10));
    setValue('weightKg', next, { shouldValidate: true, shouldDirty: true });
  }

  return (
    <section
      className={`weight-log-widget card${fullWidth ? ' weight-log-widget--full' : ''}`}
      aria-label="Body weight"
    >
      <header className="weight-log-widget__header">
        <h2 className="weight-log-widget__title">Body weight</h2>
        <Link
          to="/review"
          className="btn-link weight-log-widget__review-link"
          aria-label="Review my week"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
        >
          Week <ArrowRight />
        </Link>
      </header>

      <p className="weight-log-widget__last">
        {lastQuery.isLoading
          ? ' '
          : last
            ? `Last logged: ${formatWeight(last.weightKg)} · ${timeAgo(last.loggedAt)}`
            : 'Log your first weight to start tracking.'}
      </p>
      {last && !hideWhenLoggedToday && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button
            type="button"
            className="btn-link"
            onClick={() => {
              setEditingId(last.id);
              setValue('weightKg', last.weightKg);
              setFocus('weightKg');
            }}
          >
            Correct latest
          </button>
          <button
            type="button"
            className="btn-link text-red"
            disabled={deleteMutation.isPending}
            onClick={() => {
              if (window.confirm('Delete the latest weight reading?')) {
                void deleteMutation.mutateAsync(last.id).then(() => toast.success('Deleted'));
              }
            }}
          >
            Delete latest
          </button>
        </div>
      )}

      <form className="weight-log-widget__form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-group weight-log-widget__field">
          <label htmlFor="weight-log-input" className="visually-hidden">
            Weight (kg)
          </label>
          <div className="weight-log-widget__input-wrap">
            <input
              id="weight-log-input"
              type="number"
              step={0.1}
              min={20}
              max={300}
              inputMode="decimal"
              placeholder="kg"
              aria-invalid={!!errors.weightKg}
              {...register('weightKg', {
                setValueAs: (v) =>
                  v === '' || v === null || v === undefined || Number.isNaN(Number(v))
                    ? undefined
                    : Number(v),
              })}
            />
            <div className="weight-log-widget__stepper" aria-hidden>
              <button
                type="button"
                tabIndex={-1}
                onClick={() => step(1)}
                aria-label="Increase weight"
              >
                <Chevron dir="up" />
              </button>
              <button
                type="button"
                tabIndex={-1}
                onClick={() => step(-1)}
                aria-label="Decrease weight"
              >
                <Chevron dir="down" />
              </button>
            </div>
          </div>
          <button type="submit" className="weight-log-widget__submit" disabled={submitting}>
            {submitting ? 'Saving…' : editingId ? 'Save correction' : 'Log weight'}
          </button>
        </div>
        {errors.weightKg && (
          <span role="alert" className="form-error">
            {errors.weightKg.message ?? 'Enter a value between 20 and 300 kg'}
          </span>
        )}
      </form>
    </section>
  );
}

/** Thin, round-capped chevron for the custom weight stepper. */
function Chevron({ dir }: { dir: 'up' | 'down' }) {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden focusable="false">
      <path
        d={dir === 'up' ? 'M4 10l4-4 4 4' : 'M4 6l4 4 4-4'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
